import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import { BrowserRouter } from 'react-router';

import '@fontsource/pixelify-sans/400.css';
import '@fontsource/pixelify-sans/600.css';

import { App } from './App';
import { SessionProvider } from './auth/SessionProvider';
import { RegistryProvider } from './registry/RegistryProvider';
import './theme.css';
import './blocks/blocks.css';
import './menus/menus.css';

const container = document.getElementById('root');
if (container === null) {
  throw new Error('index.html is missing #root');
}

// SessionProvider sits INSIDE the router: the sign-in button needs the current location
// to build its returnTo, and the badge is rendered from the shell's header.
//
// RegistryProvider wraps the app rather than the editor route because the catalog is one
// fetch of one file: hoisting it here means a kit form with a dozen item tiles loads the
// dump once, and no widget has to take it as a prop. It costs read mode one request that
// 304s forever after, and it is what keeps `BlockProps` exactly as the block registry
// defined it - item widgets are not special-cased anywhere in the form machinery.
createRoot(container).render(
  <StrictMode>
    <BrowserRouter>
      <SessionProvider>
        <RegistryProvider>
          <App />
        </RegistryProvider>
      </SessionProvider>
    </BrowserRouter>
  </StrictMode>,
);

/*
 * THE AUDIO SERVICE WORKER (plan §9.8), in a build only: `vite dev` serves `/sw.js` as a module and the
 * dev library changes under it, so development runs with none. The worker intercepts `/audio/…` and
 * nothing else (see `audio/sw.ts`), so registering it cannot make any page code stale. `updateViaCache:
 * 'none'` makes the browser's update check go to the network, so a new deploy's worker is picked up on
 * the next navigation rather than after the HTTP cache decides.
 */
if (import.meta.env.PROD && 'serviceWorker' in navigator) {
  void navigator.serviceWorker.register('/sw.js', { scope: '/', updateViaCache: 'none' }).catch(() => {
    /* a private window, or a browser that refuses: audio still plays, just without the cache */
  });
}
